/**
 * NIGHTBOUND - small client hooks.
 *
 * These are display-only. The shared engine is used here purely to keep the
 * bars moving smoothly between server polls; the server remains the source of
 * truth and every poll overwrites whatever was projected locally.
 */

import { useEffect, useMemo, useState } from 'react'
import { HOUR_MS } from '../../shared/constants'
import { projectNeeds } from '../../shared/petState'
import type { CreatureNeeds, CreatureView } from '../../shared/types'

/** A ticking timestamp. Re-renders the consuming component on each interval. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])
  return now
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  )
  useEffect(() => {
    const list = window.matchMedia(query)
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/** True on layouts wide enough for the three-column desktop shelter. */
export function useDesktopLayout(): boolean {
  return useMediaQuery('(min-width: 1024px)')
}

/** The OS-level reduced-motion preference. */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)')
}

/**
 * Needs projected a little way past the last server value, purely so the bars
 * drift rather than sit frozen. Capped at one poll interval.
 */
export function useProjectedNeeds(
  creature: CreatureView | null,
  clockOffsetMs: number,
): CreatureNeeds | null {
  const now = useNow(15_000)
  return useMemo(() => {
    if (!creature) return null
    if (creature.sleeping) return creature.needs
    const anchor = new Date(creature.lastStateCalculation).getTime()
    if (!Number.isFinite(anchor)) return creature.needs
    const serverNow = now + clockOffsetMs
    const hours = Math.max(0, Math.min((serverNow - anchor) / HOUR_MS, 1 / 60))
    // `projectNeeds` deliberately only drifts the decaying needs; fitness is
    // carried over unchanged from the last authoritative value.
    return { ...projectNeeds(creature.needs, hours), fitness: creature.needs.fitness }
  }, [creature, now, clockOffsetMs])
}

/** Seconds until an action is usable again, ticking locally. */
export function useCooldown(
  creature: CreatureView | null,
  action: string,
  clockOffsetMs: number,
): number {
  const now = useNow(1000)
  return useMemo(() => {
    const entry = creature?.cooldowns?.[action]
    if (!entry) return 0
    const readyAt = new Date(entry.readyAt).getTime()
    if (!Number.isFinite(readyAt)) return 0
    return Math.max(0, Math.ceil((readyAt - (now + clockOffsetMs)) / 1000))
  }, [creature, action, now, clockOffsetMs])
}

/**
 * Resolve the first candidate URL that actually loads. Returns undefined while
 * probing and on total failure, so callers can fall back to procedural art.
 */
export function useFirstAvailableAsset(candidates: string[]): {
  src: string | undefined
  probing: boolean
} {
  const key = candidates.join('|')
  const [state, setState] = useState<{ src: string | undefined; probing: boolean }>({
    src: undefined,
    probing: candidates.length > 0,
  })

  useEffect(() => {
    let cancelled = false
    setState({ src: undefined, probing: candidates.length > 0 })

    const tryAt = (index: number) => {
      if (cancelled) return
      const candidate = candidates[index]
      if (!candidate) {
        setState({ src: undefined, probing: false })
        return
      }
      const image = new Image()
      image.onload = () => {
        if (!cancelled) setState({ src: candidate, probing: false })
      }
      image.onerror = () => tryAt(index + 1)
      image.src = candidate
    }

    tryAt(0)
    return () => {
      cancelled = true
    }
    // `key` sums up the candidate list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return state
}
